import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Grid, Typography } from "@mui/material";

import FilterBtn from "../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../Assets/Icons/btn-filters-up.png";
import AddUserIcon from "../../../../Assets/Icons/add-user-icon.png";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../Utils";
import { createFilterHandler } from "../../../../Utils/FilterUtils";
import CustomLoadingOverlay from "../../../../Components/CustomLoadingOverlay";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import { useBranch } from "../../../../Hooks/useBranch";
import { buildApiQuery } from "../../../../Utils/apiQueryBuilder";

import { getColumns } from "./columns";
import ArchiveDeviceModal from "./ArchiveDeviceModal";
import StatusTabs from "./StatusTabs";
import Filter from "./Filter";
import StatusChangeConfirmationModal from "./StatusChangeConfirmationModal";

const INITIAL_FILTER_STATE = {
  name: "",
  imei: "",
  type: "",
  active: "",
  // lastUsed: "",
  employeeName: "",
  assignedUserId: "",
};

const fieldMappings = {
  name: ["device_name", "$contains"],
  imei: ["device_id", "$eq"],
  type: ["device_type", "$eq"],
  active: ["device_status", "$eq"],
  // lastUsed: ["lastUsed", "$eq"],
  // employeeName: ["employeeName", "$eq"],
  assignedUserId: ["assigned_user_id", "$eq"],
};

const valueTransformers = {
  // Convert boolean active to 1/0
  active: (value) => {
    if (value === "Active") return 1;
    if (value === "Inactive") return 0;
    return value; // Return original value if not boolean
  },
};

// Derive initial state from URL
const getInitialDevice = (location) => {
  // Prioritize activeRole from location.state if available
  if (location.state?.activeRole) {
    return location.state.activeRole;
  }

  // Fallback to deriving role from pathname
  const path = location?.pathname;
  if (path && path.includes("/devices/all")) return "All";

  // Extract role from path
  const pathParts = path && path.split("/");
  if (pathParts && pathParts.length >= 3) {
    const deviceSlug = pathParts[2];
    const deviceFormatted = deviceSlug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return deviceFormatted;
  }
  return "All";
};

export default function DevicesTable({ status = "all" }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [activeStatus, setActiveStatus] = useState(status);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
const [allowAdd , setAllowAdd] = useState(0)
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);

  const [sortModel, setSortModel] = useState([]);

  const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);
  const [refreshList, setRefreshList] = useState(false); // To refetch the list after some actions/api call without page reload manually by user

  const { currentBranch } = useBranch();

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh(); // to refresh after token expire

  const fetchDevices = async (
    page = 1,
    pageSize = 20,
    filters = submittedData,
    sortInfo = sortModel
  ) => {
    const endpoint = `devices/${status}`;

    const apiUrl = buildApiQuery(endpoint, {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      filters: submittedData,
      sortModel,
      fieldMappings,
      valueTransformers,
    });

    try {
      setLoading(true);

      const res = await httpclient.get(apiUrl);

      const { data } = res;
setAllowAdd(data);
      // console.log(`this is ${status} test`,data)

      if (data.status === 200) {
        setDevices(data.data);
        setUsers(data.users);
        setRowCount(data.meta?.total || data.count || 0);

        setRefreshList(false);
      } else {
        showNotification("error", data.msg);
      }
    } catch (error) {
      if (error.response.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices(
      paginationModel.page + 1,
      paginationModel.pageSize,
      submittedData,
      sortModel
    );
  }, [
    refreshList,
    currentBranch,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    submittedData,
  ]);

  // Update state when URL changes
  useEffect(() => {
    const newStatus = getInitialDevice(location);
    setActiveStatus(newStatus);
  }, [location.pathname]);

  const handleChangeStatus = () => {
    setLoading(true);

    const API_URL = `devices/status/${selectedDevice.id}`;

    try {
      httpclient.post(API_URL).then(({ data }) => {
        if (data.status === 200) {
          showNotification("success", data.message);
          setSelectedDevice("");
          setOpenStatusChangeModal(false);
          setRefreshList(true);
        } else {
          showNotification("error", data.message);
        }
      });
    } catch (error) {
      console.log(error, "error changing status");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleArchive = () => {
    setLoading(true);

    const API_URL = `devices/archive/${selectedDevice.id}`;

    try {
      httpclient.post(API_URL).then(({ data }) => {
        if (data.status === 200) {
          showNotification("success", data.message);
          setSelectedDevice("");
          setOpen(false);
          setRefreshList(true);
        } else {
          showNotification("error", data.message);
        }
      });
    } catch (error) {
      console.log(error, "error archiving device");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setDevices,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `devices/${status}`,
    fieldMappings,
    valueTransformers,
    refresh,
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    // Create new filter data without the removed field
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    // Reset the field in filterData (UI state)
    newFilterData[field] = "";

    // Remove the field from submittedData (API state)
    delete newSubmittedData[field];

    // Update states
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    // Trigger API call with remaining filters
    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleAddDevice = () => {
    navigate("/devices/add", { state: { users: users, activeStatus } });
  };

  const handleEdit = (device) => {
    setSelectedDevice(device);
    navigate(`/devices/edit/${device.device_id}`, {
      state: { device, users, activeStatus },
    });
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    // Reset to the first page when sorting changes
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const filteredRows = useMemo(() => {
    if (!devices) return [];

    return devices.filter((row) => {
      // 1. Filter by `activeStatus` (All/Assigned/Unassigned/Archived)
      if (activeStatus !== "All") {
        switch (activeStatus) {
          case "Assigned":
            if (row.device_type !== "Assigned") return false;
            break;
          case "Unassigned":
            if (row.device_type !== "Unassigned") return false;
            break;
          case "Archived":
            if (row.device_type !== "Archived") return false;
            break;
          default:
            break;
        }
      }

      // 2. Filter by `submittedData` (search/filter inputs)
      if (
        submittedData.name &&
        !row.device_name
          .toLowerCase()
          .includes(submittedData.name.toLowerCase())
      ) {
        return false;
      }

      if (
        submittedData.imei &&
        !row.device_id.toLowerCase().includes(submittedData.imei.toLowerCase())
      ) {
        return false;
      }

      if (submittedData.type && row.device_type !== submittedData.type) {
        return false;
      }

      if (
        submittedData.employeeName &&
        (!row.assigned_user ||
          !row.assigned_user
            .toLowerCase()
            .includes(submittedData.employeeName.toLowerCase()))
      ) {
        return false;
      }

      if (
        submittedData.assignedUserId &&
        row.assigned_user_id !== submittedData.assignedUserId
      ) {
        return false;
      }

      return true; // Include row if all filters pass
    });
  }, [devices, activeStatus, submittedData]);

  const modifiedFilteredRows =
    filteredRows &&
    filteredRows.map((row) => ({
      name: row.device_name,
      imei: row.device_id,
      type: row.device_type,
      active: row.device_status,
      employeeName: row.assigned_user,
      assignedUserId: row.assigned_user_id,
      ...row,
    }));

  const columns = getColumns(
    setSelectedDevice,
    setOpenStatusChangeModal,
    handleEdit,
    setOpen,
    handleArchive
  );

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#fff",
          padding: "24px 16px",
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <StatusTabs activeStatus={activeStatus} />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            {allowAdd.allow_add === 1 && (

            
            <Button
              variant="contained"
              sx={{
                borderRadius: "4px",
                backgroundColor: "#7152F3",
                "&:hover": {
                  backgroundColor: "#5c34fa",
                },
              }}
              startIcon={<img src={AddUserIcon} alt="Add User icon" />}
              onClick={handleAddDevice}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                }}
              >
                Add New Device
              </Typography>
            </Button>
            )}
            <img
              src={filterOpen ? FilterBtnUp : FilterBtn}
              alt="Filter"
              style={{ cursor: "pointer" }}
              onClick={() => setFilterOpen(!filterOpen)}
            /> 
          </Grid>
          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
          />
        </Grid>

        <DataGrid
          rows={modifiedFilteredRows || []}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 20,
              },
            },
          }}
          loading={loading}
          slots={{
            loadingOverlay: CustomLoadingOverlay,
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "14px",
              fontWeight: 500,
              color: "#48505E",
            },
          }}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel} // Controlled sort model
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
        />
        {openStatusChangeModal && (
          <StatusChangeConfirmationModal
            open={openStatusChangeModal}
            cancel={() => setOpenStatusChangeModal(false)}
            title="Change Status"
            item={selectedDevice?.name}
            loading={loading}
            selectedDevice={selectedDevice}
            handleChangeStatus={handleChangeStatus}
          />
        )}
        {open && (
          <ArchiveDeviceModal
            open={open}
            handleClose={handleClose}
            selectedDevice={selectedDevice}
            handleArchive={handleArchive}
            loading={loading}
          />
        )}
      </Box>
    </Box>
  );
}
